/**
 * LINE ニュース編集画面の「配信」タブ。
 * 仕様書: docs/01_spec/spec_line_bot.md A-10.5
 *
 * 表示内容:
 * - 配信サマリ: status / sentAt / sentRecipientCount / recipientCount / lastError
 *
 * 注: 「配信記録」テーブル + 受信者リスト展開は当初実装したが、
 *     LINE Broadcast API の仕様で個別受信者 ID は取得不可、
 *     Multicast では targetUserIds が判明済（カードに既に紐付け済）のため、
 *     2026-04-26 にユーザー判断でセクション削除。サマリのみで運用。
 *
 * 表示条件: 親側で status === 'sent' || 'failed' のときだけマウントする想定。
 */
import { AlertCircle, CheckCircle2, Clock, XCircle } from 'lucide-react';

import type { LineNewsRecord } from '@/hooks/useLineNews';
import { formatDateTime } from '@/lib/firestoreTime';

interface Props {
  news: LineNewsRecord;
}

function describeRecipients(news: LineNewsRecord): string {
  if (news.targetUserIds.length === 0) {
    return news.sentRecipientCount && news.sentRecipientCount > 0
      ? `全友だち（${news.sentRecipientCount} 名 / LINE Insight）`
      : '全友だち（数値取得待ち）';
  }
  return `指定 ${news.targetUserIds.length} 名`;
}

export function NewsDeliveryTab({ news }: Props) {
  return (
    <div className="space-y-6">
      <section className="rounded border border-[var(--border)] bg-[var(--bg-elevated)] p-4 space-y-3">
        <div className="flex items-center gap-2">
          {news.status === 'sent' ? (
            <CheckCircle2 className="size-4 text-[var(--accent-green)]" />
          ) : news.status === 'failed' ? (
            <XCircle className="size-4 text-[var(--accent-red)]" />
          ) : (
            <Clock className="size-4 text-[var(--text-muted)]" />
          )}
          <span className="font-semibold text-[13px]">配信サマリ</span>
        </div>
        <dl className="grid grid-cols-[120px_1fr] gap-y-1.5 text-xs">
          <dt className="text-[var(--text-muted)]">ステータス</dt>
          <dd className="font-medium">{news.status}</dd>

          <dt className="text-[var(--text-muted)]">配信日時</dt>
          <dd>{news.sentAt ? formatDateTime(news.sentAt) : '—'}</dd>

          <dt className="text-[var(--text-muted)]">配信先</dt>
          <dd>{describeRecipients(news)}</dd>

          <dt className="text-[var(--text-muted)]">試行回数</dt>
          <dd>{news.attempts ?? 0}</dd>

          {news.lastError && (
            <>
              <dt className="text-[var(--text-muted)]">最終エラー</dt>
              <dd className="text-[var(--accent-red)] flex items-start gap-1">
                <AlertCircle className="size-3 mt-0.5 flex-shrink-0" />
                <span className="break-all">{news.lastError}</span>
              </dd>
            </>
          )}
        </dl>
        {news.targetUserIds.length === 0 && news.status === 'sent' && (
          <p className="text-[10px] text-[var(--text-muted)] leading-relaxed pt-1 border-t border-[var(--border)]">
            ※ Broadcast 配信の人数は LINE Insight API（前日 0:00 時点の集計）から取得します。
            集計完了前は「全友だち配信完了」のみ表示され、後日数値が反映されます。
          </p>
        )}
      </section>
    </div>
  );
}
